import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { usePermissions } from "components/Hook";

import { ManageUser } from "store/actions/Settings/ManageUser";
import { UserAdditionalInfo } from "store/actions/UserAdditionalInfo";

import { CustomerUserRow, getCustomerUsers } from "services/customerOwnership";

export const NO_ELIGIBLE_ADMIN_MESSAGE = "No other admin available. Add a new user to transfer ownership.";

const useAccountOwnership = () => {
  const dispatch = useDispatch();
  const manageUser = useSelector((state: any) => state.ManageUser?.manageUser);
  const { loggedInUser, loggedInUserType } = usePermissions();
  const { userId, customerId, permissions } = loggedInUser || {};

  const canOwn = loggedInUserType === "customer" && !!customerId && !!permissions?.includes("CustomerAdmin");

  const [users, setUsers] = useState<CustomerUserRow[]>([]);

  const refresh = useCallback(async () => {
    if (!canOwn) return;
    setUsers(await getCustomerUsers(customerId));
  }, [canOwn, customerId]);

  useEffect(() => {
    let isActive = true;
    if (canOwn) {
      getCustomerUsers(customerId).then((list) => {
        if (isActive) setUsers(list);
      });
    }
    return () => {
      isActive = false;
    };
  }, [canOwn, customerId]);

  const isOwner = users.some((user) => user.userId === userId && user.mainUser === true);

  const candidates = users.filter(
    (user) =>
      user.userId !== userId && !user.mainUser && !user.firstLogin && !!user.permissions?.includes("CustomerAdmin")
  );

  const onTransferred = useCallback(async () => {
    await refresh();
    dispatch(UserAdditionalInfo(`customer/${customerId}/info`));
    if (manageUser?.ok && manageUser.payload?.list) {
      dispatch(
        ManageUser({
          request: `v2/customer/${customerId}/customerUsers`,
          rowsPerPage: 50,
          page: 0,
          sortBy: { field: "First_Name", direction: "asc" },
        })
      );
    }
  }, [refresh, dispatch, customerId, manageUser]);

  return { isOwner, candidates, refresh, onTransferred };
};

export default useAccountOwnership;
